/**
 * Centralized color definitions for the Squid application.
 *
 * Category colours live in the design-token layer (`theme/tokens.js`) so that
 * charts, chips and this module cannot drift apart. This file adds the
 * user-override behaviour (localStorage) on top of those tokens.
 */

import { categoryPalette } from '../theme/tokens';

// Default color palette for effort categories, derived from design tokens.
export const DEFAULT_CATEGORY_COLORS = Object.fromEntries(
  Object.entries(categoryPalette).map(([name, token]) => [name, token.main])
);

// Standard category order for consistent display.
//
// These are the six current work types plus the honest residual. `Project`,
// `Learn` and `Uncategorized` are retired and must not reappear here — they
// no longer exist in the allocation schema. `Change` was reintroduced by
// owner request to catch maintenance/upgrade/patch tickets that were
// otherwise falling into Unattributed.
export const CATEGORY_ORDER = [
  'Company Projects',
  'Internal Projects',
  'Toil',
  'Unplanned',
  'Learn/Levelup',
  'Change',
  'Unattributed',
];

// Load custom colors from localStorage if available
const loadCustomColors = () => {
  try {
    const customColors = localStorage.getItem('categoryColors');
    return customColors ? JSON.parse(customColors) : {};
  } catch (error) {
    console.error('Error loading custom colors:', error);
    return {};
  }
};

// Get category colors with custom overrides
export const CATEGORY_COLORS = {
  ...DEFAULT_CATEGORY_COLORS,
  ...loadCustomColors()
};

// Save custom colors to localStorage
export const saveCustomColors = (colors) => {
  try {
    localStorage.setItem('categoryColors', JSON.stringify(colors));
    // Update the runtime colors
    Object.assign(CATEGORY_COLORS, colors);
    return true;
  } catch (error) {
    console.error('Error saving custom colors:', error);
    return false;
  }
};

// Reset to default colors
export const resetCategoryColors = () => {
  try {
    localStorage.removeItem('categoryColors');
    // Reset runtime colors to defaults
    Object.keys(CATEGORY_COLORS).forEach(key => {
      CATEGORY_COLORS[key] = DEFAULT_CATEGORY_COLORS[key] || CATEGORY_COLORS[key];
    });
    return true;
  } catch (error) {
    console.error('Error resetting colors:', error);
    return false;
  }
};

/**
 * Helper function to get a category color with fallback
 * This is the preferred method to get category colors throughout the application
 * 
 * @param {string} category - Category name
 * @returns {string} - Hex color code
 */
export const getCategoryColor = (category) => {
  if (!category) return CATEGORY_COLORS.Unattributed;
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.Other;
};

/**
 * Helper function to get a color with alpha transparency
 * @param {string} color - Hex color code
 * @param {number} alpha - Alpha value between 0 and 1
 * @returns {string} - Color with alpha channel
 */
export const getColorWithAlpha = (color, alpha = 0.5) => {
  if (!color) return alpha === 1 ? '#9e9e9e' : `#9e9e9e${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
  try {
    return alpha === 1 ? color : `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
  } catch (error) {
    return alpha === 1 ? '#9e9e9e' : `#9e9e9e${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
  }
};

/**
 * Get a consistent array of colors for charts
 * This ensures we always get the latest colors, including any runtime changes
 */
export const getChartColors = () => {
  // Brand-tinted extras for series beyond the known work types, so an
  // unexpected category never renders in off-brand purple/pink/brown.
  return CATEGORY_ORDER.map((category) => getCategoryColor(category)).concat([
    '#7C5CFF',
    '#0FB5AE',
    '#8DB0F5',
  ]);
};

/**
 * Create chart datasets with consistent category colors
 * Use this to ensure all charts use the same colors for the same categories
 * 
 * @param {Array} data - The data array
 * @param {Function} getValue - Function to extract the value (e.g., effort) from each data item 
 * @param {Function} getCategory - Function to extract the category from each data item
 * @param {string} label - Label for the dataset
 * @param {boolean} withAlpha - Whether to use alpha transparency
 * @returns {Object} Chart dataset with consistent colors
 */
export const createCategoryDataset = (data, getValue, getCategory, label = 'Effort (hours)', withAlpha = false) => {
  return {
    label,
    data: data.map(item => Math.round(getValue(item) * 10) / 10),
    backgroundColor: data.map(item => {
      const category = getCategory(item);
      const color = getCategoryColor(category);
      return withAlpha ? getColorWithAlpha(color, 0.8) : color;
    }),
    borderColor: data.map(item => getCategoryColor(getCategory(item))),
    borderWidth: 1
  };
};

// DEPRECATED: Use getChartColors() instead for up-to-date colors
// This is kept for backward compatibility only
export const CHART_COLORS = getChartColors(); 